import { rem } from "@mantine/core";
import { closeModal, openContextModal } from "@mantine/modals";
import IconChevronLeft from "@src/assets/icons/lucide/chevron-left.svg?react";
import IconHeartHandshake from "@src/assets/icons/lucide/heart-handshake.svg?react";
import IconAlertTriangle from "@src/assets/icons/tabler/alert-triangle.svg?react";
import { LoadingSpinner } from "@src/components/loading-spinner";
import { useMyReferralProfile } from "../queries/get-my-referral-profile";
import { ReferralTable } from "./components/referral-table";
import { RewardsSection } from "./components/rewards-section";

let modalId: string | null = null;

export function openInviteHistoryModal() {
	modalId = openContextModal({
		modal: "buttonsModal",
		padding: rem(20),
		centered: true,
		withCloseButton: false,
		zIndex: 5000,
		classNames: {
			content:
				"relative shadow-2xl border border-border-subtle bg-surface-default mx-4 sm:mx-0 rounded-2xl max-h-[90dvh] flex flex-col",
			body: "overflow-y-auto flex-1",
		},
		onClose: () => {
			modalId = null;
		},
		innerProps: {
			children: (
				<ReferralHistoryModalContent onClose={closeInviteHistoryModal} />
			),
		},
	});
}

export function closeInviteHistoryModal() {
	if (modalId) {
		closeModal(modalId);
	}
}

export function ReferralHistoryModalContent({
	onClose,
}: { onClose: () => void }) {
	const {
		data: referralProfile,
		isPending,
		isError,
		refetch: refetchInviteHistory,
	} = useMyReferralProfile();
	const referralUsers = referralProfile?.referrals ?? [];

	return (
		<div className="flex flex-col">
			<button
				type="button"
				onClick={onClose}
				className="flex items-center gap-2 mb-4 text-sm text-text-primary hover:text-text-secondary transition-colors cursor-pointer self-start"
				aria-label="Back to referral modal"
			>
				<IconChevronLeft className="w-4 h-4" />
				<span>Back</span>
			</button>

			<h2 className="text-sm font-bold text-text-primary mb-5">
				Invitation History
			</h2>

			<RewardsSection />

			<div className="border border-border-default rounded-lg overflow-hidden">
				<div className="grid grid-cols-[1fr_auto] gap-4 px-4 py-2 border-b border-border-subtle bg-surface-subtle">
					<div className="text-xs font-bold text-text-secondary">Email</div>
					<div className="text-xs font-bold text-text-secondary text-right">
						Register Date
					</div>
				</div>

				{isPending ? (
					<LoadingState />
				) : isError ? (
					<ErrorState onRefresh={() => refetchInviteHistory()} />
				) : referralUsers.length === 0 ? (
					<EmptyState />
				) : (
					<ReferralTable referralUsers={referralUsers} />
				)}
			</div>

			<p className="text-xs text-text-secondary font-normal mt-5 text-center">
				You will get benefits automatically for every valid referral.
			</p>
		</div>
	);
}

function LoadingState() {
	return (
		<div className="flex flex-col items-center justify-center py-12 px-4">
			<div className="flex items-center justify-center w-12 h-12 rounded-full bg-surface-subtle mb-3">
				<LoadingSpinner
					width={24}
					height={24}
					style={{ color: "var(--ui-color-text-muted)" }}
				/>
			</div>
			<p className="text-sm text-text-secondary text-center">Loading...</p>
		</div>
	);
}

function EmptyState() {
	return (
		<div className="flex flex-col items-center justify-center py-12 px-4">
			<div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#eecc0c33] mb-3">
				<IconHeartHandshake className="w-6 h-6 text-[#EECC0C] stroke-[#EECC0C]" />
			</div>
			<p className="text-sm text-text-secondary text-center">
				There are no invited users yet
			</p>
		</div>
	);
}

function ErrorState({ onRefresh }: { onRefresh: () => void }) {
	return (
		<div className="flex flex-col items-center justify-center py-12 px-4">
			<div className="flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft mb-3">
				<IconAlertTriangle className="w-6 h-6 text-danger stroke-danger" />
			</div>
			<p className="text-sm text-text-secondary text-center mb-4">
				Some error occurred
			</p>
			<button
				type="button"
				onClick={onRefresh}
				className="px-4 py-2 text-sm font-medium text-text-primary bg-surface-subtle rounded-md border border-border-default transition-colors hover:bg-surface-hover-subtle hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
				aria-label="Refresh invitation history"
			>
				Refresh
			</button>
		</div>
	);
}
